import React from 'react';
import { Link } from 'react-router-dom';
import {
  StyledPostTitle,
  StyledParagraph,
  StyledSectionHeading,
  StyledOL,
} from '../styled';

export default function Article() {
  return (
    <>
      <StyledPostTitle id="play-football-manager-on-phone">
        Play football manager on your phone — free, no app store, no Netflix
      </StyledPostTitle>
      <StyledParagraph big>
        Search play football manager on phone and you usually hit a wall:
        a Netflix Games exclusive, an app-store download, or a soccer
        manager on phone that lives behind energy timers. The ask is
        simpler — a football manager without Netflix that actually fits
        a commute or a couch.
      </StyledParagraph>
      <StyledParagraph big>
        The Open League (TOL) is that lane: a football manager mobile
        free in your browser. Short seasons, Discord match nights, and
        human transfers. Open the site on Android or iPhone, set your
        XI, and you’re in a league with people — no subscription gate,
        no install required.
      </StyledParagraph>

      <StyledSectionHeading>
        Why “FM on phone” usually means a Netflix wall
      </StyledSectionHeading>
      <StyledParagraph big>
        The official way to play Football Manager on a phone right now
        is FM26 Mobile — a Netflix Games exclusive. You need an active
        Netflix membership, then you install through the store. That’s
        a fair deal if you already subscribe and want a deep solo save.
        It’s a dead end if you searched for a football manager without
        Netflix.
      </StyledParagraph>
      <StyledParagraph big>
        The rest of the store is mostly OSM-style soccer manager on
        phone apps: easy to tap, often free to start, and frequently
        padded with ads or pay-to-skip. None of that is “open the
        browser and manage a club with humans tonight.”
      </StyledParagraph>
      <StyledParagraph big>
        If you want the 400-hour SI rabbit hole, play Football Manager
        on a machine that can hold it. If you want to play football
        manager on phone without a streaming plan, you need a different
        product.
      </StyledParagraph>

      <StyledSectionHeading>
        What you need for a real football manager on mobile
      </StyledSectionHeading>
      <StyledParagraph big>
        A browser football manager mobile is “real” when the phone
        isn’t a toy version of a spreadsheet:
        <StyledOL>
          <li>
            Tactics that matter — formation, effort, mindset you can
            set in a minute and feel on match night.
          </li>
          <li>
            Other humans — a table, deals, and banter, not only an AI
            that never talks back.
          </li>
          <li>
            Sessions that fit a phone evening — check in, set the XI,
            come back for highlights. Not another 400-hour save.
          </li>
          <li>
            No subscription wall — football manager mobile free should
            mean the core loop isn’t gated by Netflix or an energy bar.
          </li>
          <li>
            Android and iPhone in the browser — a free football manager
            for Android / iPhone that doesn’t make you pick a store
            first.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        That’s the brief TOL is built for. For the wider free-online
        picture, see{' '}
        <Link to="/blog/free-football-manager-online">
          free football manager online
        </Link>
        ; for the social layer, see{' '}
        <Link to="/blog/discord-football-manager">
          Discord football manager
        </Link>
        .
      </StyledParagraph>

      <StyledSectionHeading>
        How The Open League plays on your phone
      </StyledSectionHeading>
      <StyledParagraph big>
        Open theopenleague.com in Safari or Chrome. Register, claim a
        club, and manage from the web portal. Discord is where match
        text, negotiations, and the season actually live.
      </StyledParagraph>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Daily matches — full sims on a schedule; play-by-play text
            lands where your group already hangs out.
          </li>
          <li>
            Three-week seasons — promote, relegate, then{' '}
            <Link to="/user-guide/off-season">youth camp</Link> and deals in the
            break.
          </li>
          <li>
            <Link to="/user-guide/trading">Human transfers</Link> — negotiate
            with other managers; formalize in the club portal.
          </li>
          <li>
            Simple tactics — formations, effort, mindset. Easy on a
            phone screen; hard against people.
          </li>
          <li>
            Optional store apps if you prefer an icon — the browser
            path is enough to play football manager on phone tonight.
          </li>
        </StyledOL>
      </StyledParagraph>
      <StyledParagraph big>
        New managers: skim the{' '}
        <Link to="/user-guide/start">user guide</Link>, set your XI,
        and catch the first match night. For a longer genre map, there’s
        also our{' '}
        <Link to="/blog/online-football-manager-games">
          overview of online football manager games
        </Link>
        .
      </StyledParagraph>

      <StyledSectionHeading>
        Phone FM options compared
      </StyledSectionHeading>
      <StyledParagraph big>
        Short and honest — these are different sports, not a ranked
        list:
      </StyledParagraph>
      <div style={{ overflowX: 'auto', marginBottom: 20 }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: 'Archivo, sans-serif',
            fontWeight: 300,
            fontSize: 16,
            lineHeight: '26px',
            letterSpacing: '0.4px',
            textAlign: 'left',
          }}
        >
          <thead>
            <tr>
              <th style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #000' }}>
                Option
              </th>
              <th style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #000' }}>
                How you play
              </th>
              <th style={{ padding: '8px 12px 8px 0', borderBottom: '1px solid #000' }}>
                Wall
              </th>
              <th style={{ padding: '8px 0', borderBottom: '1px solid #000' }}>
                Best for
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                FM26 Mobile
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Netflix Games + app install
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Active Netflix plan
              </td>
              <td style={{ padding: '10px 0', verticalAlign: 'top' }}>
                Deep solo FM if you already subscribe
              </td>
            </tr>
            <tr>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                OSM-style apps
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                App store soccer manager on phone
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Ads / IAP common
              </td>
              <td style={{ padding: '10px 0', verticalAlign: 'top' }}>
                Casual daily taps
              </td>
            </tr>
            <tr>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Solitary browser sims
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Browser, mostly vs AI
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Mixed; often free-to-start
              </td>
              <td style={{ padding: '10px 0', verticalAlign: 'top' }}>
                A solo save on the go
              </td>
            </tr>
            <tr>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                The Open League
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                Browser football manager mobile + Discord
              </td>
              <td style={{ padding: '10px 12px 10px 0', verticalAlign: 'top' }}>
                None — free, no Netflix
              </td>
              <td style={{ padding: '10px 0', verticalAlign: 'top' }}>
                Short seasons with human transfers
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <StyledParagraph big>
        FM is still the deepest single-player football management
        experience. TOL isn’t a clone — it’s the social, phone-first
        alternative for people who want a league story more than a
        streaming add-on.
      </StyledParagraph>

      <StyledSectionHeading>
        Getting started in under five minutes
      </StyledSectionHeading>
      <StyledParagraph big>
        <StyledOL>
          <li>
            Open <Link to="/">theopenleague.com</Link> in your phone
            browser and register / log in.
          </li>
          <li>
            Join a club in a timezone that matches your evenings.
          </li>
          <li>
            Read{' '}
            <Link to="/user-guide/start">Getting started</Link>, set
            your XI, and you’re ready for match night.
          </li>
          <li>
            Open Discord for highlights and deals — that’s the social
            season, not a second app store.
          </li>
        </StyledOL>
      </StyledParagraph>

      <StyledSectionHeading>FAQ</StyledSectionHeading>
      <StyledParagraph big>
        <strong>Do I need Netflix to play football manager on phone?</strong>{' '}
        Not with TOL. Official FM26 Mobile is a Netflix exclusive; The
        Open League is a football manager without Netflix.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Do I need the app store?</strong> No. The browser path
        is enough. Store apps exist if you want an icon.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Does it work on Android and iPhone?</strong> Yes — a
        free football manager for Android / iPhone in the mobile
        browser (Safari or Chrome).
      </StyledParagraph>
      <StyledParagraph big>
        <strong>Is The Open League free?</strong> Yes — football manager
        mobile free to play, with optional convenience features. The
        core loop isn’t gated.
      </StyledParagraph>
      <StyledParagraph big>
        <strong>How long is a session?</strong> Check-ins are short:
        set tactics, watch Discord highlights, come back tomorrow.
        Seasons last about three weeks.
      </StyledParagraph>

      <StyledParagraph big>
        A good way to play football manager on phone should open in the
        browser you already have — no Netflix wall, no install, a
        match night with people. That’s the Open League bet.
      </StyledParagraph>

      <StyledParagraph big>
        Ready to manage? <Link to="/">Play TOL now</Link>
        {' — open it on your phone, then catch Discord match night. '}
        <Link to="/user-guide/start">User guide</Link>
        {' — '}
        <Link to="/blog/discord-football-manager">
          Discord football manager
        </Link>
        .
      </StyledParagraph>
    </>
  );
}
